import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import MenuItem from '@material-ui/core/MenuItem'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { Pagination } from '@material-ui/lab'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import PageHeader from '../../components/PageHeader'
import { PAYMENT_INVESTIGATIONS_QUERY } from '../../graphql/paymentInvestigations'
import { formatSessionTime } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import {
  PaymentInvestigationKind,
  PaymentInvestigationPage,
  PaymentInvestigationReason
} from '../../types/paymentInvestigations'
import useStyles from './index.styles'
import {
  defaultInvestigationFilters,
  INVESTIGATION_KINDS,
  parseInvestigationNumber,
  readInvestigationFilters,
  writeInvestigationFilters
} from './investigationFilters'
import { FOLLOW_UP_STATES, stateLabel } from './labels'

const PER_PAGE = 20
const kindLabels: Record<PaymentInvestigationKind, string> = {
  FundingAllocationUnreconstructable: 'Payment allocation needs investigation',
  WalletReleaseAttributionUnknown: 'Wallet release needs investigation'
}
const reasonLabels: Record<PaymentInvestigationReason, string> = {
  MissingRoomAllocation: 'Room payment allocation is unavailable',
  MissingHistory: 'Payment history is incomplete',
  CorruptLedger: 'Payment records are inconsistent',
  BoundExceeded: 'More payment history needs review',
  UnprovenWalletSourceMapping:
    'The wallet release outcome or its payment allocation is unverified'
}

/** Source investigations never offer a settlement mutation or an inferred refund amount. */
export default function Investigations() {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const applied = useMemo(
    () => readInvestigationFilters(location.search),
    [location.search]
  )
  const [draft, setDraft] = useState(applied)
  const [numberInput, setNumberInput] = useState(
    applied.reservationNumber?.toString() ?? ''
  )
  const [invalid, setInvalid] = useState(false)
  const [page, setPage] = useState(1)
  useEffect(() => {
    setDraft(applied)
    setNumberInput(applied.reservationNumber?.toString() ?? '')
    setInvalid(false)
    setPage(1)
  }, [applied])
  const { data, loading, error, refetch } = useQuery<{
    paymentInvestigations: PaymentInvestigationPage
  }>(PAYMENT_INVESTIGATIONS_QUERY, {
    variables: {
      filter: {
        state: applied.state,
        ...(applied.kind ? { kind: applied.kind } : {}),
        ...(applied.reservationNumber !== null
          ? { displayReservationNumber: applied.reservationNumber }
          : {})
      },
      page,
      perPage: PER_PAGE
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
  const result = !loading && !error ? data?.paymentInvestigations : undefined
  const items = result?.items ?? []
  const pages = Math.max(1, Math.ceil((result?.total ?? 0) / PER_PAGE))
  const apply = (event: FormEvent) => {
    event.preventDefault()
    const number = numberInput.trim()
      ? parseInvestigationNumber(numberInput)
      : null
    if (numberInput.trim() && number === null) {
      setInvalid(true)
      return
    }
    history.replace({
      ...location,
      search: writeInvestigationFilters(location.search, {
        ...draft,
        reservationNumber: number
      })
    })
    setInvalid(false)
    setPage(1)
  }
  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Payment investigations')} />
      <div className={classes.header}>
        <Typography component="h1" variant="h3">
          {getTranslate('Payment investigations')}
        </Typography>
        <Button
          disabled={loading}
          onClick={() => {
            void refetch().catch(() => undefined)
          }}>
          {getTranslate('Refresh')}
        </Button>
      </div>
      <Typography component="p" color="textSecondary">
        {getTranslate(
          'Investigate missing financial evidence. Verified source data clears these records automatically. A refund or settlement entry does not clear them.'
        )}
      </Typography>
      <form className={classes.filters} onSubmit={apply} noValidate>
        <div className={classes.filterFields}>
          <SelectWithLabel
            id="investigation-state"
            fullWidth
            size="small"
            noHelperText
            label={getTranslate('State')}
            value={draft.state}
            onChange={(event) =>
              setDraft({
                ...draft,
                state: event.target.value as typeof draft.state
              })
            }>
            {FOLLOW_UP_STATES.map((state) => (
              <MenuItem key={state} value={state}>
                {stateLabel(state)}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            id="investigation-kind"
            fullWidth
            size="small"
            noHelperText
            label={getTranslate('Issue')}
            value={draft.kind}
            SelectProps={{ displayEmpty: true }}
            onChange={(event) =>
              setDraft({
                ...draft,
                kind: event.target.value as typeof draft.kind
              })
            }>
            <MenuItem value="">{getTranslate('All issues')}</MenuItem>
            {INVESTIGATION_KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {getTranslate(kindLabels[kind])}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <div>
            <InputWithLabel
              id="investigation-reservation"
              fullWidth
              inputSize="small"
              noHelperText
              label={getTranslate('Reservation Number')}
              value={numberInput}
              onChange={(event) => setNumberInput(event.currentTarget.value)}
              inputMode="numeric"
              dir="ltr"
              error={invalid}
              aria-invalid={invalid}
              aria-describedby={
                invalid ? 'investigation-number-error' : undefined
              }
            />
            {invalid && (
              <Typography
                id="investigation-number-error"
                role="alert"
                color="error"
                variant="caption">
                {getTranslate('Enter a valid reservation number.')}
              </Typography>
            )}
          </div>
        </div>
        <div className={classes.filterActions}>
          <Button
            onClick={() => {
              setDraft(defaultInvestigationFilters())
              setNumberInput('')
              setInvalid(false)
            }}>
            {getTranslate('Reset')}
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {getTranslate('Apply')}
          </Button>
        </div>
      </form>
      {error && (
        <Typography role="alert" color="error">
          {getTranslate(
            'Payment investigations could not be loaded. Refresh to retry.'
          )}
        </Typography>
      )}
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress
            role="status"
            aria-label={getTranslate('Loading')}
          />
        </div>
      ) : (
        !error &&
        result && (
          <>
            <Typography component="p">
              {getTranslate('{count} investigations found', {
                count: result.total
              })}
            </Typography>
            {items.length === 0 ? (
              <Typography component="p">
                {getTranslate('No investigations match these filters.')}
              </Typography>
            ) : (
              <div className={classes.tableWrapper}>
                <Table
                  size="small"
                  aria-label={getTranslate('Payment investigations')}>
                  <TableHead>
                    <TableRow>
                      {[
                        'Issue',
                        'Reservation Number',
                        'State',
                        'Observed at',
                        'Resolved at'
                      ].map((label) => (
                        <TableCell key={label}>{getTranslate(label)}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>{getTranslate(kindLabels[item.kind])}</div>
                          <Typography
                            variant="caption"
                            className={classes.reasonAction}>
                            {getTranslate(reasonLabels[item.reason])}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.displayReservationNumber &&
                          item.bookingReference ? (
                            <LocalizedLink
                              to={`/booking/list/${item.bookingReference}/view`}>
                              <bdi>{item.displayReservationNumber}</bdi>
                            </LocalizedLink>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{stateLabel(item.state)}</TableCell>
                        <TableCell>
                          {formatSessionTime(item.observedAt)}
                        </TableCell>
                        <TableCell>
                          {formatSessionTime(item.resolvedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {pages > 1 && (
              <div className={classes.paginationContainer}>
                <Pagination
                  count={pages}
                  page={page}
                  color="primary"
                  onChange={(_, value) => setPage(value)}
                />
              </div>
            )}
          </>
        )
      )}
    </div>
  )
}
